import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useCartSync } from "@/hooks/useCartSync";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { SkipToContent } from "@/components/SkipToContent";
import { PageLoader } from "@/components/PageLoader";
import { ScrollProgress } from "@/components/ScrollProgress";
import { Suspense, lazy, useEffect } from "react";
import { MotionConfig } from "framer-motion";
import { lazyRetry } from "@/lib/lazyRetry";
import { isEmbedPath } from "@/lib/embed";
import { getSupabase, isPasswordRecovery } from "@/integrations/supabase/lazy";
import { useWishlistStore } from "@/stores/wishlistStore";
import { useCartAbandonmentTracker } from "@/hooks/useCartAbandonmentTracker";
import { useProductComparison } from "@/components/ProductComparison";
import { useCookieBannerVisible } from "@/hooks/useCookieGate";
import { useFloatingPanels } from "@/stores/floatingPanelsStore";
import { captureEmailCampaign } from '@/lib/emailAttribution';
import { CONSENT_CHANGED_EVENT, CONSENT_KEY } from '@/lib/consent';
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy-load global widgets that aren't needed for initial paint
const ChatWidget = lazyRetry(() => import("@/components/ChatWidget").then(m => ({ default: m.ChatWidget })));
const CookieConsent = lazyRetry(() => import("@/components/CookieConsent").then(m => ({ default: m.CookieConsent })));
const CompareBar = lazyRetry(() => import("@/components/CompareBar").then(m => ({ default: m.CompareBar })));
const BackToTop = lazyRetry(() => import("@/components/BackToTop").then(m => ({ default: m.BackToTop })));
const MobileBottomNav = lazyRetry(() => import("@/components/MobileBottomNav").then(m => ({ default: m.MobileBottomNav })));
const AccessibilityWidget = lazyRetry(() => import("@/components/AccessibilityWidget").then(m => ({ default: m.AccessibilityWidget })));
const ExitIntentPopup = lazyRetry(() => import("@/components/ExitIntentPopup").then(m => ({ default: m.ExitIntentPopup })));
// Breadcrumb JSON-LD for the flat routes. Not worth a page reload if its chunk
// fails to load, so no lazyRetry: it just renders nothing.
const StaticRouteJsonLd = lazy(() => import("@/components/StaticRouteJsonLd").catch(() => ({ default: () => null })));


// Lazy-loaded route pages with auto-retry on stale chunks
const ProductDetail = lazyRetry(() => import("./pages/ProductDetail"));
const Category = lazyRetry(() => import("./pages/Category"));
const Wishlist = lazyRetry(() => import("./pages/Wishlist"));
const SearchPage = lazyRetry(() => import("./pages/Search"));

const About = lazyRetry(() => import("./pages/About"));
const Contact = lazyRetry(() => import("./pages/Contact"));
const ShippingInfo = lazyRetry(() => import("./pages/ShippingInfo"));
const ReturnsPolicy = lazyRetry(() => import("./pages/ReturnsPolicy"));
const FAQ = lazyRetry(() => import("./pages/FAQ"));
const Privacy = lazyRetry(() => import("./pages/Privacy"));
const Terms = lazyRetry(() => import("./pages/Terms"));
const Accessibility = lazyRetry(() => import("./pages/Accessibility"));
const Press = lazyRetry(() => import("./pages/Press"));
const Financing = lazyRetry(() => import("./pages/Financing"));
const TradeProgram = lazyRetry(() => import("./pages/TradeProgram"));
const DesignServicesPage = lazyRetry(() => import("./pages/DesignServicesPage"));
const Ideas = lazyRetry(() => import("./pages/Ideas"));
const Article = lazyRetry(() => import("./pages/Article"));
const Sustainability = lazyRetry(() => import("./pages/Sustainability"));
const Auth = lazyRetry(() => import("./pages/Auth"));
const Account = lazyRetry(() => import("./pages/Account"));
const Cart = lazyRetry(() => import("./pages/Cart"));
const BundleBuilder = lazyRetry(() => import("./pages/BundleBuilder"));
const Categories = lazyRetry(() => import("./pages/Categories"));
const ThankYou = lazyRetry(() => import("./pages/ThankYou"));
const BestSellers = lazyRetry(() => import("./pages/BestSellers"));
const OrderTracking = lazyRetry(() => import("./pages/OrderTracking"));
const Unsubscribe = lazyRetry(() => import("./pages/Unsubscribe"));
const GetTheLook = lazyRetry(() => import("./pages/GetTheLook"));
const Admin = lazyRetry(() => import("./pages/Admin"));
const StyleQuiz = lazyRetry(() => import("./pages/StyleQuiz"));
const RugSizeCalculator = lazyRetry(() => import("./pages/tools/RugSizeCalculator"));
const ChandelierSizeCalculator = lazyRetry(() => import("./pages/tools/ChandelierSizeCalculator"));
const DiningTableSizeCalculator = lazyRetry(() => import("./pages/tools/DiningTableSizeCalculator"));
const WallArtSizeCalculator = lazyRetry(() => import("./pages/tools/WallArtSizeCalculator"));
const LampshadeSizeCalculator = lazyRetry(() => import("./pages/tools/LampshadeSizeCalculator"));
const CoffeeTableSizeCalculator = lazyRetry(() => import("./pages/tools/CoffeeTableSizeCalculator"));
const BarStoolHeightCalculator = lazyRetry(() => import("./pages/tools/BarStoolHeightCalculator"));
const SofaDoorFitCalculator = lazyRetry(() => import("./pages/tools/SofaDoorFitCalculator"));
const ChristmasTreeSizeCalculator = lazyRetry(() => import("./pages/tools/ChristmasTreeSizeCalculator"));
const ToolsIndex = lazyRetry(() => import("./pages/tools/ToolsIndex"));
const EmbedTool = lazyRetry(() => import("./pages/tools/EmbedTool"));
const StyleBoardResult = lazyRetry(() => import("./pages/StyleBoardResult"));

const queryClient = new QueryClient();

const AppContent = () => {
  useCartSync();
  useScrollToTop();
  useCartAbandonmentTracker();
  const location = useLocation();
  useEffect(() => {
    const capture = () => captureEmailCampaign(location.search);
    const storage = (event: StorageEvent) => { if (event.key === CONSENT_KEY) capture(); };
    capture();
    window.addEventListener(CONSENT_CHANGED_EVENT, capture);
    window.addEventListener('storage', storage);
    return () => { window.removeEventListener(CONSENT_CHANGED_EVENT, capture); window.removeEventListener('storage', storage); };
  }, [location.search, location.pathname]);
  const setWishlistUserId = useWishlistStore(state => state.setUserId);
  const comparison = useProductComparison();
  const isHomePage = location.pathname === "/";
  const isEmbed = isEmbedPath(location.pathname);
  // Every flat route is one path segment (/about, /faq, /ideas …), so product,
  // category and guide pages never fetch the StaticRouteJsonLd chunk.
  const isFlatPath = /^\/[a-z0-9-]+\/?$/.test(location.pathname);
  const cookieBannerVisible = useCookieBannerVisible();
  const chatUsed = useFloatingPanels((state) => state.chatUsed);
  const navigate = useNavigate();

  // A password-reset link signs the visitor in wherever it sends them back to;
  // the form that sets the new password lives on /auth. The URL hash goes along:
  // it holds the single-use tokens, and the Supabase client, which loads after
  // this effect, has not read them yet. Once, on arrival: sending the visitor
  // there again on every navigation would trap them on that form.
  useEffect(() => {
    if (isPasswordRecovery() && window.location.pathname !== "/auth") {
      navigate({ pathname: "/auth", hash: window.location.hash }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    getSupabase().then((supabase) => {
      if (cancelled) return;
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        setWishlistUserId(session?.user?.id || null);
      });
      unsubscribe = () => subscription.unsubscribe();
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!cancelled) setWishlistUserId(session?.user?.id || null);
      });
    });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  return (
    <>
      <SkipToContent />
      {isFlatPath && (
        <Suspense fallback={null}>
          <StaticRouteJsonLd pathname={location.pathname} />
        </Suspense>
      )}
      <Suspense fallback={<PageLoader />}>
        <div id="main-content">
        <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/product/:handle" element={<ProductDetail />} />
        <Route path="/categories" element={<Categories />} />
        {/* Legacy / indexed slug aliases — keep inbound links working */}
        <Route path="/category/rugs" element={<Navigate to="/category/area-rugs" replace />} />
        {/* Its product type no longer exists; the supplier files these as "Outdoor Hanging Lights". */}
        <Route path="/category/outdoor-lighting" element={<Navigate to="/category/outdoor-hanging-lights" replace />} />
        {/* No sale collection exists: Shopify supplies no real compare-at prices. */}
        <Route path="/category/sale" element={<Navigate to="/categories" replace />} />
        <Route path="/category/on-sale" element={<Navigate to="/categories" replace />} />
        <Route path="/category/:slug/sale" element={<Navigate to=".." relative="path" replace />} />
        <Route path="/category/:slug" element={<Category />} />
        <Route path="/wishlist" element={<Wishlist />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/shipping" element={<ShippingInfo />} />
        <Route path="/returns" element={<ReturnsPolicy />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/accessibility" element={<Accessibility />} />
        <Route path="/press" element={<Press />} />
        <Route path="/financing" element={<Financing />} />
        <Route path="/trade-program" element={<TradeProgram />} />
        <Route path="/design-services" element={<DesignServicesPage />} />
        <Route path="/ideas" element={<Ideas />} />
        <Route path="/ideas/:slug" element={<Article />} />
        {/* Destinations used by live Pinterest pins that never existed as pages. */}
        <Route path="/ideas/entryway-guide" element={<Navigate to="/ideas/entryway-styling-console-mirror-landing-zone" replace />} />
        <Route path="/ideas/layered-lighting-guide" element={<Navigate to="/ideas/layered-lighting-101" replace />} />
        <Route path="/ideas/rug-mistakes" element={<Navigate to="/ideas/rug-mistakes-rooms-feel-smaller" replace />} />
        <Route path="/ideas/color-theory-for-interiors" element={<Navigate to="/ideas/neutral-color-palette" replace />} />
        <Route path="/collections/all" element={<Navigate to="/categories" replace />} />
        <Route path="/bundles" element={<Navigate to="/design-your-room" replace />} />
        <Route path="/sustainability" element={<Sustainability />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/account" element={<Account />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/design-your-room" element={<BundleBuilder />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/best-sellers" element={<BestSellers />} />
        <Route path="/order-tracking" element={<OrderTracking />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/get-the-look" element={<GetTheLook />} />
        <Route path="/style-quiz" element={<StyleQuiz />} />
        <Route path="/style-quiz/result" element={<StyleBoardResult />} />
        <Route path="/tools/rug-size-calculator" element={<RugSizeCalculator />} />
        <Route path="/tools/chandelier-size-calculator" element={<ChandelierSizeCalculator />} />
        <Route path="/tools/dining-table-size-calculator" element={<DiningTableSizeCalculator />} />
        <Route path="/tools/wall-art-size-calculator" element={<WallArtSizeCalculator />} />
        <Route path="/tools/lampshade-size-calculator" element={<LampshadeSizeCalculator />} />
        <Route path="/tools/coffee-table-size-calculator" element={<CoffeeTableSizeCalculator />} />
        <Route path="/tools/bar-stool-height-calculator" element={<BarStoolHeightCalculator />} />
        <Route path="/tools/sofa-door-fit-calculator" element={<SofaDoorFitCalculator />} />
        <Route path="/tools/christmas-tree-size-calculator" element={<ChristmasTreeSizeCalculator />} />
        <Route path="/tools" element={<ToolsIndex />} />
        {/* A calculator framed on another site: no header, footer or site widgets. */}
        <Route path="/embed/:tool" element={<EmbedTool />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<NotFound />} />
        </Routes>
        </div>
      </Suspense>

        {/* Beside the page's Suspense boundary, not inside it. Inside, these
            lazy widgets were still loading when the page's own chunk arrived,
            so React saw the page as "still suspended" and held it back until
            500 ms after the loader had appeared (its fallback throttle). */}
        {!isHomePage && !isEmbed && (
          <>
            <ScrollProgress />
            <Suspense fallback={null}>
              <MobileBottomNav />
              <BackToTop />
              {/* Hidden behind the cookie banner unless opened from the phone nav;
                  once opened it stays mounted, so closing it there keeps the
                  conversation. Its own boundary, so mounting it never blanks the nav. */}
              <Suspense fallback={null}>
                {(!cookieBannerVisible || chatUsed) && <ChatWidget />}
              </Suspense>
              <CompareBar />

              <ExitIntentPopup />
              {!cookieBannerVisible && <AccessibilityWidget />}
            </Suspense>
          </>
        )}
        {/* The homepage keeps its floating buttons off, but its menu still opens
            both panels (Header): phones had no way to either from there. */}
        {isHomePage && (
          <Suspense fallback={null}>
            {chatUsed && <ChatWidget launcher={false} />}
            {!cookieBannerVisible && <AccessibilityWidget launcher={false} />}
          </Suspense>
        )}
        {/* On every page, the homepage included: the privacy policy promises a
            Decline choice, and ad landings arrive on the homepage. */}
        {/* Not inside another site's frame: consent there stays at the denied
            default, so the embed sets no analytics cookies to ask about. */}
        {!isEmbed && (
          <Suspense fallback={null}>
            <CookieConsent />
          </Suspense>
        )}
    </>
  );
};

const App = () => (
  <HelmetProvider>
    <MotionConfig reducedMotion="user">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
    </MotionConfig>
  </HelmetProvider>
);

export default App;
